import { useTranslation } from 'react-i18next'
import { LibraryCollectionCard, LibraryCollectionCover } from '@digital-twins/shared-types'

import { CollectionSharingBadge } from '@/components/library/CollectionSharingBadge'
import { LibraryCountPhrase } from '@/components/library/LibraryCountPhrase'
import { CollectionCoverMosaic } from '@/components/library/MosaicCollectionCard'
import {
  LIBRARY_CARD_CHIP_CLASS,
  LIBRARY_CARD_META_CLASS,
  LIBRARY_CARD_TITLE_CLASS,
} from '@/components/library/libraryCardStyles'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

export interface LibraryFeaturedBannerProps {
  collections: Array<LibraryCollectionCard>
}

/** Collections looked at for the feature, newest first (the list's order). */
const FEATURE_CANDIDATES = 6

/**
 * Three 260px tracks plus the grid's two 24px column gaps. At this width the
 * resource grid is three across, so a two-track lead still leaves a card
 * beside it. Narrower, the lead is one track.
 */
const LEAD_SPAN_CLASS =
  '@[828px]/library-docs:col-span-2 @[828px]/library-docs:grid-cols-[minmax(0,1.15fr)_minmax(0,0.9fr)] @[828px]/library-docs:items-center @[828px]/library-docs:gap-4'

/**
 * The collection the Library features: among the newest curated ones (a
 * saved filter is a view, not an editorial set), the first with a cover
 * image, else the newest. Null when there is none.
 */
export function pickFeaturedCollection(collections: Array<LibraryCollectionCard>): LibraryCollectionCard | null {
  const curated = collections.filter((collection) => collection.origin !== 'saved_filter').slice(0, FEATURE_CANDIDATES)
  const withImage = curated.find((collection) => collection.cover.some((cover) => Boolean(cover.imageUrl)))
  return withImage ?? curated[0] ?? null
}

/** Photos first, then document pages, then covers that have no image. */
function leadCovers(cover: Array<LibraryCollectionCover>): Array<LibraryCollectionCover> {
  const isDocument = (item: LibraryCollectionCover) => item.kind === 'document' || item.kind === 'dataset'
  const pictured = cover.filter((item) => Boolean(item.imageUrl))
  const rest = cover.filter((item) => !item.imageUrl)
  return [
    ...pictured.filter((item) => !isDocument(item)),
    ...pictured.filter((item) => isDocument(item)),
    ...rest,
  ]
}

/**
 * The Library home's featured collection, in the resource grid: two tracks
 * once the grid is three across, one track below that. The mosaic and the
 * name use the collection card's language. The whole lead opens the collection.
 */
export function LibraryFeaturedBanner(props: LibraryFeaturedBannerProps) {
  const { collections } = props

  const { t } = useTranslation('app')

  const featured = pickFeaturedCollection(collections)

  if (!featured) {
    return null
  }

  const resourceTotal = Object.values(featured.resourceCounts).reduce((sum, count) => sum + count, 0)
  const audienceCount = featured.resourceCounts.audience

  return (
    <Link
      href={ROUTES.knowledgeCollection(featured.token)}
      aria-label={t('library.collections.featured.label')}
      className={cn(
        'group col-span-1 grid min-w-0 grid-cols-1 content-center rounded-[14px] border border-primary/10 bg-primary/10 p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        LEAD_SPAN_CLASS,
      )}
      data-testid="library-featured"
    >
      <CollectionCoverMosaic
        cover={leadCovers(featured.cover)}
        testId="library-featured-cover"
        className="aspect-[16/10] shrink-0"
        showKindLabels
      />
      <div className="flex min-w-0 flex-col justify-center gap-2 pt-3 @[828px]/library-docs:pt-0">
        <Badge variant="muted" className={cn(LIBRARY_CARD_CHIP_CLASS, 'w-fit bg-background/80 text-foreground')} data-testid="library-featured-label">
          {t('library.collections.featured.label')}
        </Badge>
        <h2 className={LIBRARY_CARD_TITLE_CLASS} data-testid="library-featured-title">
          {featured.name}
        </h2>
        <p className={LIBRARY_CARD_META_CLASS} data-testid="library-featured-meta">
          <LibraryCountPhrase noun="resources" count={resourceTotal} />
          <span aria-hidden="true">·</span>
          <LibraryCountPhrase noun="topics" count={featured.topicCount} />
          {audienceCount > 0 ? (
            <>
              <span aria-hidden="true">·</span>
              <LibraryCountPhrase noun="audiences" count={audienceCount} />
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <CollectionSharingBadge
            sharingMode={featured.sharingMode}
            showLabel
            labelFor={
              featured.sharingMode === 'organization'
                ? { organization: t('library.collections.featured.shared') }
                : undefined
            }
          />
        </p>
      </div>
    </Link>
  )
}
